import { useMemo } from 'react'

import type { ExpensesExpensableFilter } from 'generated/resources/graphql'

import { mapFiltersToVariables } from './filters/filterUtils'

interface UseDataCollectionFilterExtractionOptions {
  /**
   * Default employeeIds to use when no owner filter is applied.
   * Used in contexts where we need to scope data to specific employees.
   */
  defaultEmployeeIds?: number[]
  /**
   * Whether this is a group view (affects filter mapping behavior)
   */
  isGroupView?: boolean
}

interface ExtractedFilters {
  /**
   * Extracted employeeIds with fallback logic applied
   */
  employeeIds: number[] | undefined
  /**
   * Extracted additional filters for the status counts query
   */
  filter: ExpensesExpensableFilter | undefined
}

export const useDataCollectionFilterExtraction = (
  currentFilters: Record<string, unknown> | undefined,
  options: UseDataCollectionFilterExtractionOptions = {}
): ExtractedFilters => {
  const { defaultEmployeeIds, isGroupView = false } = options

  // Extract employeeIds and additional filters from current filter state
  const { employeeIds: filterEmployeeIds, filter } = useMemo(() => {
    return mapFiltersToVariables(currentFilters, { isGroupView })
  }, [currentFilters, isGroupView])

  // Apply fallback logic: use filter employeeIds if set, otherwise use defaults
  // This ensures consistent behavior between status counts and data queries
  const employeeIds = useMemo(() => {
    if (!defaultEmployeeIds) {
      return filterEmployeeIds
    }
    return filterEmployeeIds?.length ? filterEmployeeIds : defaultEmployeeIds
  }, [filterEmployeeIds, defaultEmployeeIds])

  return {
    employeeIds,
    filter,
  }
}
